import styled, { css } from 'styled-components';
import { DesignStyle } from './types';
import { customClassNames } from './utils';

const increaseClassSpecificity = (
    className: string,
    specificity: number = 2
) => {
    return `.${className}`.repeat(specificity);
};

const commonBookerContainerStyles = css`
    /* make dry run message visible inside booker */
    div.fixed:has([data-testid='dry-run-msg']) {
        position: absolute;
        z-index: 1;
    }

    ${increaseClassSpecificity(customClassNames.bookerContainer)} {
        border-radius: 6px;
        width: 100% !important;
        height: 100% !important;
        isolation: isolate;

        > div[class*='grid-area:meta'] {
            position: static !important;
        }

        form {
            [data-testid='select-control'] {
                > div {
                    height: var(--cal-spacing-8);
                    font-size: small;
                }

                div:has(> svg) {
                    height: auto;
                }

                // fix for dropdown chevron position
                div:has(> div > svg) {
                    top: 50%;
                    position: relative;
                    translate: 0 -50%;
                }

                input {
                    height: auto;
                }
            }

            // cal default styles
            textarea,
            input[type='text'],
            input[type='email'],
            input[type='tel'],
            input[type='number'],
            input[type='date'],
            input[type='time'],
            input[type='datetime-local'] {
                font-size: 0.875rem;
                background-color: var(--cal-bg, #fff);
                border-color: var(--cal-border);
                border-width: 1px;
                width: 100%;
                margin-bottom: var(--cal-spacing-2);
                height: var(--cal-spacing-8);
                padding-top: var(--cal-spacing-2);
                padding-bottom: var(--cal-spacing-2);

                &:focus {
                    border-color: var(--brand-color);
                }
            }

            [data-fob-field-name='guests'] {
                input {
                    background-color: transparent;
                    height: 100%;
                    margin: 0;
                    border: none;
                }

                div:has(> input):focus-within {
                    border-width: 1px;
                }
            }
        }
    }
`;

const commonEventMetaStyles = css`
    .${customClassNames.atomsWrapper}:not(.booker-skeleton,.booker-markup)
        .${customClassNames.eventMetaCustomClassNames.eventMetaContainer} {
        > div {
            display: flex;
            flex-direction: column;

            // hide empty block (placeholder)
            > div[class='h-6'] {
                display: none;
            }

            // TODO: ask cal to add more class names
            // ensure these blocks are always at the start, keep order of internal booker elements and then show pricing:
            // 0) list of avatars
            > ul {
                order: -1;
            }
            // 1) providers names
            > p {
                order: -1;
            }
            // 2) event meta title
            > .${customClassNames.eventMetaCustomClassNames.eventMetaTitle} {
                order: -1;
            }
            // 3) event meta description
            > div[data-testid='event-meta-description'] {
                order: -1;
            }
            // 4) booking pricing meta section
            > div:has(> .cal-booking-price-meta-section) {
                order: -1;
            }
        }
    }

    .${customClassNames.eventMetaCustomClassNames.eventMetaTitle} {
        font-size: 18px !important;
        color: unset !important;
    }
`;

const themeButtonStyles = css`
    background-color: var(--btn-bg-color);
    background-image: var(--btn-bg-image);
    background-position: var(--btn-bg-position);
    background-repeat: var(--btn-bg-repeat);
    background-size: var(--btn-bg-size);
    border-color: var(--btn-border-color);
    border-bottom-color: var(--btn-border-b-color);
    border-left-color: var(--btn-border-l-color);
    border-right-color: var(--btn-border-r-color);
    border-top-color: var(--btn-border-t-color);
    border-radius: var(--btn-border-radius);
    border-bottom-left-radius: var(--btn-border-bl-radius);
    border-bottom-right-radius: var(--btn-border-br-radius);
    border-top-left-radius: var(--btn-border-tl-radius);
    border-top-right-radius: var(--btn-border-tr-radius);
    border-width: var(--btn-border-width);
    border-bottom-width: var(--btn-border-b-width);
    border-left-width: var(--btn-border-l-width);
    border-right-width: var(--btn-border-r-width);
    border-top-width: var(--btn-border-t-width);
    direction: var(--btn-text-direction);
    text-align: var(--btn-text-align);
    color: var(--btn-text-color);
    font-family: var(--btn-text-font-family);
    font-size: var(--btn-text-font-size);
    font-weight: var(--btn-text-font-weight);
    text-decoration: var(--btn-text-decoration);
    font-style: var(--btn-text-font-style);
    padding: 8px 14px;

    &:hover {
        background: var(--btn-hover-bg);
        border-color: var(--btn-hover-border-color);
        border-bottom-color: var(--btn-hover-border-b-color);
        border-left-color: var(--btn-hover-border-l-color);
        border-right-color: var(--btn-hover-border-r-color);
        border-top-color: var(--btn-hover-border-t-color);
        color: var(--btn-hover-text-color) !important;
        font-weight: var(--btn-hover-text-font-weight);
        text-decoration: var(--btn-hover-text-decoration);
        font-style: var(--btn-hover-text-font-style);
    }
`;

const themeBookerContainerStyles = css`
    .${customClassNames.bookerContainer} {
        --cal-text-color: var(--color_1);
        --cal-border-subtle: var(--color_1);
        --cal-border: var(--color_1);
        --cal-border-emphasis: var(--color_1);

        border-color: var(--color_1);
        background-color: var(--color_3);
        color: var(--color_1);

        .react-tel-input,
        .flag-dropdown {
            background-color: inherit !important;
        }

        label {
            color: var(--color_1);

            * {
                color: var(--color_1);
            }
        }

        [data-testid='select-control'] {
            > div {
                background-color: var(--color_3);
            }

            * {
                color: var(--color_1);
            }

            input {
                border-width: 0 !important;
            }
        }

        textarea,
        input {
            background-color: var(--color_3);
            border-color: var(--color_1);
            border-width: 1px;

            &:not(:is(:focus, :hover)) {
                box-shadow: none;
            }

            &::placeholder {
                color: var(--color_1);
                opacity: 0.25;
            }
        }

        [data-fob-field-name='guests'] {
            input {
                background-color: transparent !important;
                height: 100%;
            }

            div:has(> input) {
                background-color: var(--color_3);
            }
        }
    }
`;

const themeEventMetaStyles = css`
    .${customClassNames.eventMetaCustomClassNames.eventMetaTitle} {
        color: var(--color_1) !important;
    }

    .${customClassNames.eventMetaCustomClassNames.eventMetaContainer} {
        svg,
        p {
            color: var(--color_1);
        }
    }
`;

const themeDatePickerStyles = css`
    .${customClassNames.datePickerCustomClassNames.datePickerContainer} {
        button {
            color: var(--color_1);
        }

        button[data-disabled='false']:not(
                .${customClassNames.datePickerCustomClassNames
                        .datePickerDatesActive}
            ) {
            color: var(--color_3);
            background-color: var(--color_1);
        }
    }

    .${customClassNames.datePickerCustomClassNames.datePickerDays} {
        color: var(--color_1);
        font-size: 0.7rem;
    }

    .${customClassNames.datePickerCustomClassNames.datePickerTitle} {
        color: var(--color_1);
        font-weight: 500;
    }

    .${customClassNames.datePickerCustomClassNames.datePickerDatesActive} {
        background-color: var(--color_4);
        color: var(--color_1);

        &:hover {
            border-color: var(--color_1);
        }
    }

    .${customClassNames.datePickerCustomClassNames.datePickerToggle} {
        color: var(--color_1);

        &:disabled {
            opacity: 0.4;
        }
    }
`;

const themeAvailableTimeSlotsStyles = css`
    .${customClassNames.availableTimeSlotsCustomClassNames
            .availableTimeSlotsHeaderContainer} {
        background-color: transparent;
    }

    .${customClassNames.availableTimeSlotsCustomClassNames
            .availableTimeSlotsTitle} {
        color: var(--color_1);
        font-weight: 500;
        font-size: 1rem;
        line-height: 1.5rem;
    }

    .${customClassNames.availableTimeSlotsCustomClassNames.availableTimes} {
        border-radius: var(--btn-border-radius);
        border-color: var(--color_1);

        &:not(:disabled):hover {
            background-color: var(--color_4) !important;
        }
    }

    .${customClassNames.availableTimeSlotsCustomClassNames
            .availableTimeSlotsTimeFormatToggle} {
        background-color: transparent;
        border-color: var(--color_1);

        button {
            color: var(--color_1);
        }

        button[aria-checked='true'] {
            background-color: var(--color_5);
        }
    }
`;

const themeConfirmationStyles = css`
    button.${customClassNames.confirmStep.confirmButton},
        button.${customClassNames.confirmStep.backButton} {
        ${themeButtonStyles}
    }

    button[data-testid='add-guests'] {
        color: var(--color_1);
        border: unset;

        &:not(:disabled):hover {
            background-color: transparent;
        }
    }
`;

export const CalBookingWrapper = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;
    width: 100%;

    .booker-wrapper {
        display: flex;
        width: 100%;
        height: 100%;
    }

    .${customClassNames.atomsWrapper} {
        height: 100%;
        width: 100%;
        text-align: initial;
    }

    html:not(:has(#cal-booking-css)) & {
        .${customClassNames.atomsWrapper}.booker-skeleton,
            .${customClassNames.atomsWrapper}.booker-markup {
            display: none;
        }
    }

    &.booker-loading {
        .${customClassNames.atomsWrapper}:not(.booker-skeleton) {
            display: none;
        }
    }

    &.booker-ready {
        .${customClassNames.atomsWrapper}:not(.booker-skeleton) {
            display: block;
        }
    }

    ${commonBookerContainerStyles}
    ${commonEventMetaStyles}

    &.${DesignStyle.SITE_THEME} {
        ${themeBookerContainerStyles}
        ${themeEventMetaStyles}
        ${themeDatePickerStyles}
        ${themeAvailableTimeSlotsStyles}
        ${themeConfirmationStyles}
    }
`;

export const containerQuery = (widgetId: string) => `
    [id="${widgetId}"] ${increaseClassSpecificity(
    customClassNames.atomsWrapper
)} {
        container-type: inline-size;
        container-name: atoms-wrapper;
    }

    @container atoms-wrapper (max-width: 819px) {
        [id="${widgetId}"] ${increaseClassSpecificity(
    customClassNames.bookerContainer
)} {
            --booker-meta-width: 100%;
            --booker-main-width: 100%;
            --booker-timeslots-width: 100%;
            display: flex !important;
            flex-direction: column;
        }

        [id="${widgetId}"] ${increaseClassSpecificity(
    customClassNames.bookerContainer
)} > div {
            border: unset;
            height: auto;
            width: 100%;
        }
    }

    @container atoms-wrapper (min-width: 820px) {
        [id="${widgetId}"] ${increaseClassSpecificity(
    customClassNames.bookerContainer
)} {
            --booker-meta-width: 1fr;
            --booker-main-width: 2fr;
            --booker-timeslots-width: 1fr;
            
            width: 100% !important;
        }

        [id="${widgetId}"] ${increaseClassSpecificity(
    customClassNames.bookerContainer
)}:has([class*='grid-area:timeslots']) {
            grid-template-columns: var(--booker-meta-width) var(--booker-main-width) var(--booker-timeslots-width) !important;
        }
    }

    @container atoms-wrapper (min-width: 1500px) {
        [id="${widgetId}"] ${increaseClassSpecificity(
    customClassNames.bookerContainer
)} {
            --booker-main-width: 750px;
        }
    }
`;
